import React, { useEffect, useMemo, useState } from 'react';

import { InlineSpinner } from '@/components';
import { ExclamationTriangleIcon } from '@/components/icons';
import ChallengeCreateForm from '@/forms/challenge-create-form/ChallengeCreateForm';
import { GameOption, fetchGameByDescriptor } from '@/hooks/useGamesSearch';
import {
  ChallengeDetail,
  getRewardGameJson,
  mapChallengeToFormValues,
} from './mapChallengeToFormValues';

const ChallengeCloneForm = ({ challenge }: { challenge: ChallengeDetail }) => {
  const [games, setGames] = useState<(GameOption | null)[] | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all(
      challenge.challengeRewards.map((reward) => {
        const json = getRewardGameJson(reward.challengeRewardType);
        return json ? fetchGameByDescriptor(json) : null;
      }),
    ).then((result) => {
      if (!cancelled) {
        setGames(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [challenge.challengeRewards]);

  const mapped = useMemo(
    () => (games ? mapChallengeToFormValues(challenge, games) : null),
    [challenge, games],
  );

  if (!mapped) {
    return <InlineSpinner />;
  }

  const { values, skippedRewards } = mapped;
  const hasClonedRewards = (values.rewards?.length ?? 0) > 0;

  return (
    <ChallengeCreateForm
      initialValues={values}
      notice={
        <div className="flex rounded-md border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900 dark:bg-opacity-20 dark:text-yellow-300">
          <ExclamationTriangleIcon className="mt-0.5 mr-3 h-5 w-5 flex-shrink-0 text-yellow-500 dark:text-yellow-400" />
          <div className="space-y-1">
            <p>Cloned from &quot;{challenge.name}&quot;</p>
            {(hasClonedRewards || skippedRewards.length > 0) && (
              <ul className="list-disc pl-5 space-y-1">
                {hasClonedRewards && (
                  <li>
                    Reward claim types default to <strong>Claimable</strong>,
                    review them before saving.
                  </li>
                )}
                {skippedRewards.length > 0 && (
                  <li>
                    Rewards could not be cloned because their game was not
                    found, please add them again:{' '}
                    <strong>{skippedRewards.join(', ')}</strong>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      }
    />
  );
};

export default ChallengeCloneForm;
